/* Notifications panel behind the header bell.
 *
 * The bell previously rendered a red dot when `fleetAlerts` held anything
 * unread and had NO click handler at all — it told you something needed
 * attention and then refused to take you anywhere. That is worse than having no
 * bell, because it teaches people the badge means nothing.
 *
 * This is the same alert data as the High Risk page, in a short recent list:
 * newest first, unread first, tap to open the bus, "See all" for the full page.
 */

import React, { useMemo } from 'react';
import {
  FaExclamationTriangle, FaGasPump, FaRoute, FaSatelliteDish, FaCarSide,
  FaInfoCircle, FaBell, FaTimes,
} from 'react-icons/fa';
import type { FleetAlertRow } from '../../fleet/useFleetAlerts';
import { severityOf, labelOf, kindOf, severityRank, relativeTime } from '../../fleet/alertMeta';

const KIND_ICON: Record<string, React.ReactElement> = {
  incident: <FaExclamationTriangle />,
  fuel: <FaGasPump />,
  route: <FaRoute />,
  gps: <FaSatelliteDish />,
  traffic: <FaCarSide />,
  info: <FaInfoCircle />,
};

export interface NotificationsPopoverProps {
  alerts: FleetAlertRow[];
  /** busId → plate, so a row can say which bus without a second lookup. */
  plateByBusId: Record<string, string>;
  onClose: () => void;
  onOpenBus: (busId: string) => void;
  /** Open THIS alert in the High Risk screen (preferred). Falls back to
      [onOpenBus] when absent. A fuel-review notification used to land the
      owner on the bus page's Today tab with no sign of the alert it came
      from (Gabriel, 2026-08-22). */
  onOpenAlert?: (alertId: string, busId: string) => void;
  onOpenAll: () => void;
  onMarkRead: (alertId: string) => void | Promise<void>;
  onMarkAllRead: () => void | Promise<void>;
}

/** How many rows the panel shows before deferring to the full page. */
const MAX_ROWS = 12;

export default function NotificationsPopover({
  alerts, plateByBusId, onClose, onOpenBus, onOpenAlert, onOpenAll, onMarkRead, onMarkAllRead,
}: NotificationsPopoverProps) {
  const rows = useMemo(() => {
    /* Unread first, then worst first, then newest. An unread info alert still
       beats a read critical one: the point of this panel is "what have I not
       seen", and the High Risk page is where severity leads. */
    return [...alerts]
      .sort((a, b) =>
        Number(a.read) - Number(b.read) ||
        severityRank(severityOf(a.type)) - severityRank(severityOf(b.type)) ||
        b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, MAX_ROWS);
  }, [alerts]);

  const unread = alerts.filter((a) => !a.read).length;

  return (
    <>
      <div className="fd-pop-backdrop" onClick={onClose} />
      <div className="fd-pop" style={{ top: 64, right: 16 }} role="dialog" aria-label="Notifications">
        <div className="fd-pophead">
          <FaBell style={{ color: 'var(--fd-brand-600)' }} />
          <b>Notifications</b>
          {unread > 0 && <span className="fd-pill fd-pill-info">{unread} unread</span>}
          <button
            type="button"
            className="fd-btn fd-btn-ghost fd-btn-sm"
            style={{ marginLeft: 'auto', padding: 6 }}
            onClick={onClose}
            aria-label="Close notifications"
          >
            <FaTimes />
          </button>
        </div>

        <div className="fd-popbody">
          {rows.length === 0 && (
            <div className="fd-empty" style={{ padding: '36px 20px' }}>
              <b>Nothing new</b>
              <span>No alerts have been raised for this fleet.</span>
            </div>
          )}
          {rows.map((a) => {
            const sev = severityOf(a.type);
            const igClass = sev === 'crit' ? 'fd-ig-crit' : sev === 'warn' ? 'fd-ig-warn' : 'fd-ig-brand';
            return (
              <button
                key={a.id}
                type="button"
                className={`fd-item${a.read ? ' fd-item-read' : ''}`}
                onClick={() => { if (!a.read) void onMarkRead(a.id); if (onOpenAlert) onOpenAlert(a.id, a.busId); else onOpenBus(a.busId); }}
              >
                <span className={`fd-ig ${igClass}`}>{KIND_ICON[kindOf(a.type)] ?? KIND_ICON.info}</span>
                <span className="fd-itxt">
                  <span className="fd-ititle" style={{ display: 'block' }}>{labelOf(a.type)}</span>
                  <span
                    className="fd-imeta"
                    style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {String(a.message ?? '').replace(/\bBus Bus\b/g, 'Bus')}
                  </span>
                  <span className="fd-imeta">
                    <span>{plateByBusId[a.busId] || a.busName || a.busId}</span>
                    <span className="fd-sep">·</span>
                    <span>{relativeTime(a.timestamp)}</span>
                  </span>
                </span>
                {!a.read && <span className="fd-unread" />}
              </button>
            );
          })}
        </div>

        <div className="fd-popfoot" style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="fd-btn fd-btn-quiet fd-btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={onOpenAll}>
            {/* No count: it only ever grew ("that number will continue piling
                up" — Gabriel, 2026-09-28). The unread pill above is the number
                that means something. */}
            See all
          </button>
          {unread > 0 && (
            <button type="button" className="fd-btn fd-btn-primary fd-btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={() => void onMarkAllRead()}>
              Mark all read
            </button>
          )}
        </div>
      </div>
    </>
  );
}
